/**
 * PWA install + safe update lifecycle (Appendix N) — always mounted on tenant routes.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOMER_COOKIE_BANNER_WIDTH_CLASS,
  CUSTOMER_SHELL_WIDTH_CLASS,
} from '../customerTopBarStyles.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { useCustomerAnalytics } from '../../shared/analytics/CustomerAnalyticsProvider.js';
import { ANALYTICS_PWA_EVENTS } from 'pi-kiosk-shared/analyticsEvents';
import { getRecoveryByMsId } from '../../shared/recovery/recoveryRegistry.js';
import { emitRecoveryShown } from '../../shared/recovery/recoveryAnalytics.js';
import { isPaymentPending } from './paymentPendingGate.js';
import { registerCustomerPwaServiceWorker } from './registerPwaServiceWorker.js';
import { stripCustomerPwaRecoveryQueryFromUrl } from './forceCustomerAppUpdateFromSettings.js';
import {
  clearInstallOfferShown,
  isInstallOfferDue,
  markCustomerPwaInstallEvidence,
  markInstallOfferShown,
  stampCustomerPwaInstallEvidenceIfStandalone,
} from './installOfferCooldown.js';
import {
  peekCustomerPwaInstallPrompt,
  setCustomerPwaInstallPrompt,
  subscribeCustomerPwaInstallPrompt,
  takeCustomerPwaInstallPrompt,
  type CustomerBeforeInstallPromptEvent,
} from './customerPwaInstallPromptStore.js';
import { PwaRefreshBlockingOverlay } from './PwaRefreshBlockingOverlay.js';

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS/i.test(ua);
}

function readIosGuideEligible(): boolean {
  if (typeof window === 'undefined' || !isIosSafari()) {
    return false;
  }
  return isInstallOfferDue();
}

export function PwaLifecycle(): JSX.Element | null {
  const { t } = useTranslation('customer');
  const analytics = useCustomerAnalytics();
  const [deferredPrompt, setDeferredPrompt] =
    useState<CustomerBeforeInstallPromptEvent | null>(() =>
      peekCustomerPwaInstallPrompt(),
    );
  const [installOfferVisible, setInstallOfferVisible] = useState(() => isInstallOfferDue());
  const [showIosGuide] = useState(() => readIosGuideEligible());
  const [updateReady, setUpdateReady] = useState(false);
  const [applyUpdate, setApplyUpdate] = useState<(() => void) | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const updateShownEmittedRef = useRef(false);
  const installOfferMarkedRef = useRef(false);
  const refreshLockRef = useRef(false);
  const paymentPending = isPaymentPending();

  useEffect(() => {
    const syncDeferredPrompt = (): void => {
      setDeferredPrompt(peekCustomerPwaInstallPrompt());
    };
    syncDeferredPrompt();
    return subscribeCustomerPwaInstallPrompt(syncDeferredPrompt);
  }, []);

  useEffect(() => {
    const onBeforeInstall = (event: Event): void => {
      event.preventDefault();
      setCustomerPwaInstallPrompt(event as CustomerBeforeInstallPromptEvent);
      setInstallOfferVisible(isInstallOfferDue());
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  // Evidence only — stamp when already standalone (iOS Safari ↔ A2HS share localStorage).
  useEffect(() => {
    stampCustomerPwaInstallEvidenceIfStandalone();
  }, []);

  useEffect(() => {
    const onAppInstalled = (): void => {
      markCustomerPwaInstallEvidence();
      setCustomerPwaInstallPrompt(null);
      setInstallOfferVisible(false);
    };
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  useEffect(() => {
    stripCustomerPwaRecoveryQueryFromUrl();
    registerCustomerPwaServiceWorker({
      setUpdateReady,
      setApplyUpdate,
    });
  }, []);

  const msRec077 = getRecoveryByMsId('MS-REC-077');

  useEffect(() => {
    if (!updateReady || msRec077 === undefined) {
      return;
    }
    const pending = isPaymentPending();
    emitRecoveryShown(msRec077, analytics.track.bind(analytics), {
      payment_pending: pending,
      presentation: 'banner',
    });
  }, [analytics, msRec077, updateReady]);

  useEffect(() => {
    if (!updateReady || updateShownEmittedRef.current) {
      return;
    }
    updateShownEmittedRef.current = true;
    if (isPaymentPending()) {
      analytics.track({
        eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_DEFERRED,
        screenName: 'pwa_lifecycle',
        metadata: { outcome: 'payment_pending' },
      });
      return;
    }
    analytics.track({
      eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_SHOWN,
      screenName: 'pwa_lifecycle',
    });
  }, [analytics, updateReady]);

  const showChromiumInstall =
    deferredPrompt !== null && installOfferVisible;
  const showIosInstallGuide =
    showIosGuide && deferredPrompt === null && installOfferVisible;

  useEffect(() => {
    if (!showChromiumInstall && !showIosInstallGuide) {
      return;
    }
    if (installOfferMarkedRef.current) {
      return;
    }
    installOfferMarkedRef.current = true;
    markInstallOfferShown();
  }, [showChromiumInstall, showIosInstallGuide]);

  const beginSafeRefresh = (): void => {
    if (refreshLockRef.current || isRefreshing) {
      return;
    }

    if (isPaymentPending()) {
      // Queue for after payment — no blocking overlay (reload is deferred).
      analytics.track({
        eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_APPLIED,
        screenName: 'pwa_lifecycle',
        metadata: { outcome: 'queued_payment' },
      });
      applyUpdate?.();
      return;
    }

    refreshLockRef.current = true;
    setIsRefreshing(true);
    analytics.track({
      eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_APPLIED,
      screenName: 'pwa_lifecycle',
    });

    try {
      if (applyUpdate !== null) {
        applyUpdate();
        return;
      }
      window.location.reload();
    } catch {
      refreshLockRef.current = false;
      setIsRefreshing(false);
    }
  };

  const handleInstall = async (): Promise<void> => {
    if (isRefreshing) {
      return;
    }
    const promptEvent = takeCustomerPwaInstallPrompt();
    if (promptEvent === null) {
      setInstallOfferVisible(false);
      return;
    }
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    analytics.track({
      eventName:
        choice.outcome === 'accepted'
          ? ANALYTICS_PWA_EVENTS.PWA_INSTALL_ACCEPTED
          : ANALYTICS_PWA_EVENTS.PWA_INSTALL_DISMISSED,
      screenName: 'pwa_lifecycle',
      metadata: { outcome: choice.outcome },
    });
    if (choice.outcome === 'accepted') {
      markCustomerPwaInstallEvidence();
      clearInstallOfferShown();
    }
    setInstallOfferVisible(false);
  };

  const handleDismissInstall = (): void => {
    if (isRefreshing) {
      return;
    }
    analytics.track({
      eventName: ANALYTICS_PWA_EVENTS.PWA_INSTALL_DISMISSED,
      screenName: 'pwa_lifecycle',
      metadata: { outcome: 'banner_cancel' },
    });
    setCustomerPwaInstallPrompt(null);
    setInstallOfferVisible(false);
  };

  const handleApplyUpdate = (): void => {
    beginSafeRefresh();
  };

  if (!showChromiumInstall && !showIosInstallGuide && !updateReady && !isRefreshing) {
    return null;
  }

  const applyDisabled = isRefreshing || paymentPending;

  return (
    <>
      {isRefreshing ? (
        <PwaRefreshBlockingOverlay label={t('pwa.refreshing')} />
      ) : null}
      {/* Same stack as CookieConsentBanner / ToastHost: above BottomNav + sticky cart. */}
      <div
        className={`customer-safe-area-x pointer-events-none fixed inset-x-0 bottom-[var(--customer-toast-offset,0px)] z-50 px-3 pb-2 pt-3 ${CUSTOMER_SHELL_WIDTH_CLASS} lg:[html[data-sidebar-visible]_&]:pl-56 lg:bottom-0`}
        data-testid="customer-pwa-lifecycle"
        aria-hidden={isRefreshing || undefined}
      >
        {showChromiumInstall ? (
          <Card
            padded
            className={`pointer-events-auto mb-2 ${CUSTOMER_COOKIE_BANNER_WIDTH_CLASS} border border-[var(--color-border)] shadow-[var(--shadow-popover)]`}
            role="region"
            aria-label={t('pwa.installTitle')}
          >
            <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
              {t('pwa.installTitle')}
            </h2>
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
              {t('pwa.installPrompt')}
            </p>
            <div className="mt-3 flex flex-col gap-2 min-[30rem]:flex-row min-[30rem]:items-stretch">
              <Button
                type="button"
                intent="primary"
                size="md"
                className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
                onClick={() => void handleInstall()}
                disabled={isRefreshing}
                data-testid="customer-pwa-install-button"
              >
                {t('pwa.installAction')}
              </Button>
              <Button
                type="button"
                intent="secondary"
                size="md"
                className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
                onClick={handleDismissInstall}
                disabled={isRefreshing}
                data-testid="customer-pwa-install-dismiss"
              >
                {t('pwa.installDismiss')}
              </Button>
            </div>
          </Card>
        ) : null}
        {showIosInstallGuide ? (
          <Card
            padded
            className={`pointer-events-auto mb-2 ${CUSTOMER_COOKIE_BANNER_WIDTH_CLASS} border border-[var(--color-border)] shadow-[var(--shadow-popover)]`}
            data-testid="customer-pwa-ios-guide"
            role="region"
            aria-label={t('pwa.installTitle')}
          >
            <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
              {t('pwa.installTitle')}
            </h2>
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
              {t('pwa.iosInstallGuide')}
            </p>
          </Card>
        ) : null}
        {updateReady ? (
          <Card
            padded
            className={`pointer-events-auto ${CUSTOMER_COOKIE_BANNER_WIDTH_CLASS} border border-[var(--color-border)] shadow-[var(--shadow-popover)]`}
            data-testid="recovery-surface-update-required"
            data-ms-id="MS-REC-077"
            role="region"
            aria-label={t('pwa.updateTitle')}
          >
            <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
              {t('pwa.updateTitle')}
            </h2>
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
              {paymentPending ? t('pwa.updateDeferredPayment') : t('pwa.updateReady')}
            </p>
            <div className="mt-3 flex flex-col gap-2 min-[30rem]:flex-row min-[30rem]:items-stretch">
              <Button
                type="button"
                intent="primary"
                size="md"
                className="w-full whitespace-nowrap min-[30rem]:min-w-0 min-[30rem]:flex-1"
                onClick={handleApplyUpdate}
                disabled={applyDisabled}
                aria-busy={isRefreshing || undefined}
                data-testid="customer-pwa-apply-update"
              >
                {isRefreshing ? t('pwa.refreshing') : t('pwa.updateAction')}
              </Button>
            </div>
          </Card>
        ) : null}
      </div>
    </>
  );
}
